import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { apiParameters } from 'src/app/configs/api.params';
import { distinctUntilChanged, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})

export class DmsService {

  public apiEndPoint = apiParameters.url;

  private allDms$ = new BehaviorSubject<any>(null);
  public allDms = this.allDms$.asObservable().pipe(distinctUntilChanged());
  // public apiParam = '/users/7';

  constructor(private httpClient: HttpClient) { }

  public setAllDms(dms) {
    this.allDms$.next(dms);
  }

  /**
   * Gets all dms
   * @returns All dms 
   */
  getAllDms(): Observable<any> {
    const url = apiParameters.urlV2 + '/dms';
    return this.httpClient.get(url).pipe(
      map((res:any) => {
        res.sort((a,b) => a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1)
        return res;
      })
    );
  }

   /**
   * Gets all clients dms
   * @returns All dms 
   */
    getAllClientsDms(): Observable<any> {
      const url = apiParameters.urlV2 + '/dms-client';
      return this.httpClient.get(url).pipe(
        map((res:any) => {
          res.sort((a,b) => a.name?.toLowerCase() > b.name?.toLowerCase() ? 1 : -1)
          return res;
        })
      );;
    }

  /**
   * Gets user dms
   * @param userId - user id
   * @param [profileId] - selected profile id
   * @returns User dms 
   */
  getUserDMS(userId, profileId?: string): Observable<any> {
    let searchParams = new HttpParams();

    if (profileId && profileId !== '') {
      searchParams = searchParams.append('profileId', profileId);
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
      params: searchParams
    };

    const url = apiParameters.urlV2 + '/users/' + userId + '/dms';
    return this.httpClient.get(url, httpOptions).pipe(
      map((res:any) => {
        res.sort((a,b) => a.dms.name?.toLowerCase() > b.dms.name?.toLowerCase() ? 1 : -1)
        return res;
      })
    );;
  }

   /**
   * Gets client dms
   * @param clientId - client id
   * @returns client dms 
   */
    getClientDMS(clientId: string): Observable<any> {
      const url = apiParameters.urlV2 + '/clients/' + clientId + '/dms';
      return this.httpClient.get(url).pipe(
        map((res:any) => {
          res.sort((a,b) => a.dms.name?.toLowerCase() > b.dms.name?.toLowerCase() ? 1 : -1)
          return res;
        })
      );
    }

  /**
   * Adds dms to user
   * @param userId - user id
   * @param body - form data e.g. { dmsId: selected dms id, credentials}, e.g. credentials {username: 'Tadija', password: 'pass558-0'};
   * @param profileId - selected profile id
   * @returns
   */
  addDMSToUser(userId, body, profileId?: string): Observable<any> {
    let searchParams = new HttpParams();

    if (profileId && profileId !== '') {
      searchParams = searchParams.append('profileId', profileId);
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
      params: searchParams
    };

    const url = apiParameters.urlV2 + '/users/' + userId + '/dms';
    return this.httpClient.post(url, body, httpOptions);
  }

  addDMStoClient(clientId: string, body): Observable<any> {
    return this.httpClient.post(`${apiParameters.urlV2}/clients/${clientId}/dms`, body);
  }

  /**
   * Disconnects dms
   * @param userId - user id
   * @param dmsId - dms id
   * @param profileId - selected profile id
   * @returns dms 
   */
  disconnectDMS(userId, dmsId, profileId?: string): Observable<any> {
    let searchParams = new HttpParams();

    if (profileId && profileId !== '') {
      searchParams = searchParams.append('profileId', profileId);
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
      params: searchParams
    };

    const url = apiParameters.urlV2 + '/users/' + userId + '/dms/' + dmsId;
    return this.httpClient.delete(url, httpOptions);
  }

   /**
   * Disconnects client dms
   * @param clientId - client id
   * @param dmsId - dms id
   * @returns dms 
   */
    disconnectClientDMS(clientId: string, dmsId): Observable<any> {
      const url = apiParameters.urlV2 + '/clients/' + clientId + '/dms/' + dmsId;
      return this.httpClient.delete(url);
    }

  /**
   * Updates dms
   * @param userId - user id
   * @param dmsId - dms id
   * @param body - form data e.g. { dmsId: selected dms id, credentials}, e.g. credentials {username: 'Tadija', password: 'pass558-0'};
   * @param [profileId] 
   * @returns dms 
   */
  updateUserDMS(userId, dmsId, body, profileId?: string): Observable<any> {
    let searchParams = new HttpParams();

    if (profileId && profileId !== '') {
      searchParams = searchParams.append('profileId', profileId);
    }

    const httpOptions = {
      headers: new HttpHeaders({}),
      params: searchParams
    };

    const url = apiParameters.urlV2 + '/users/' + userId + '/dms/' + dmsId;
    return this.httpClient.patch(url, body, httpOptions);
  }

  updateClientDMS(clientId: string, dmsId: string, body): Observable<any> {
    const url = apiParameters.urlV2 + '/clients/' + clientId + '/dms/' + dmsId;
    return this.httpClient.patch(url, body);
  }

  updatePicturePosition(presetId: string, targetId: string, dmsId: string, body): Observable<any> {
    const url = apiParameters.url + `/presets/${presetId}/targets/${targetId}/dms/${dmsId}`;
    return this.httpClient.patch(url, body);
  }

  /**
   * Uploads vehicle to dms
   * @param userId - user id
   * @param dmsId - dms id
   * @param vehicleId - vehicle id
   * @returns Uploaded vehicle 
   */
  uploadVehicleToDms(userId, dmsId, vehicleId): Observable<any> {
    const url = apiParameters.urlV2 + '/users/' + userId + '/dms/' + dmsId + '/upload-vehicle/' + vehicleId;
    return this.httpClient.post(url, {});
  }

  uploadVehicleToClientDms(clientId: string, dmsId: string, body): Observable<any> {
    const url = `${apiParameters.urlV2}/clients/${clientId}/dms/${dmsId}/upload-vehicle`
    return this.httpClient.post(url, body);
  }

  updateDMS(dmsId, body):Observable<any> {
    const url = apiParameters.urlV2 + '/dms/' + dmsId;
    return this.httpClient.patch(url, body)
  }

}
